import type { QuestGoal } from '../types';
import { programStanding, type ProgramRun } from './programProgress';

/**
 * Derives a linked quest goal's progress from the LINKED ENTITY's real state.
 *
 * Why this exists: a quest goal's `completed`/`progress` used to be stored state,
 * written only by the Quest UI. Completing the same habit from the Habits page or
 * Home → Today's Actions left the goal untouched, so the same real-world act counted
 * or didn't depending purely on which screen recorded it. Abilities never had this
 * problem — `RequirementTracker` derives from the source of truth — so this mirrors
 * that behaviour for quests.
 *
 * `null` means "not derivable, leave the manual toggle alone" (unlinked goals, and
 * link types we can't measure reliably).
 *
 * Attribution window: completions are counted from when the LINK was established
 * (`linkStartedAt`, falling back to the goal's `createdAt`), so attaching a habit you
 * have already kept for a year doesn't instantly bank a year of progress.
 */

export interface LinkedGoalSources {
  habits?: Array<{ id: string; completedDates?: string[]; streak?: number }>;
  /** taskStore keeps tasks bucketed by category. */
  tasksByCategory?: Record<string, Array<{ id: string; completed?: boolean; folderId?: string; pointRecord?: { earnedAt?: string } }>>;
  projects?: Array<{ id: string; status?: string; progress?: number }>;
  routines?: Array<{ id: string; completedCount?: number }>;
  events?: Array<{ id: string; completed?: boolean }>;
  /**
   * EVERY program run the user has, of any status.
   *
   * ⚠️⚠️ Deliberately NOT pre-filtered to the active one. This used to be a
   * `programProgress` callback that every caller implemented as
   * `getActiveInitiation(id)`, so a run **disappeared the moment it finished** —
   * the goal stopped being derivable at exactly the point it should have read
   * 100, froze at the day before, and never rolled up into the quest or Vision.
   * Handing over the raw runs is what stops that filter being reintroduced by a
   * caller. See `utils/programProgress.ts`.
   */
  programRuns?: ProgramRun[];
}

export interface DerivedGoalState {
  progress: number;
  completed: boolean;
}

/** The date from which a linked goal starts counting completions. */
export function linkWindowStart(goal: QuestGoal): string {
  const raw = goal.linkStartedAt || goal.createdAt || '';
  return raw.split('T')[0];
}

const pct = (current: number, target: number): DerivedGoalState => {
  const safeTarget = Math.max(1, target);
  const progress = Math.min(100, Math.round((current / safeTarget) * 100));
  return { progress, completed: progress >= 100 };
};

export function deriveLinkedGoalState(
  goal: QuestGoal,
  sources: LinkedGoalSources
): DerivedGoalState | null {
  if (!goal?.linkType || !goal?.linkId) return null;

  // `quantity` is the quest-goal equivalent of a requirement's requiredCompletions.
  const target = Math.max(1, goal.quantity || 1);
  const startDate = linkWindowStart(goal);
  const allTasks = Object.values(sources.tasksByCategory || {}).flat();

  try {
    switch (goal.linkType) {
      case 'habit': {
        const habit = sources.habits?.find(h => h?.id === goal.linkId);
        if (!habit) return null;
        const done = (habit.completedDates || []).filter(d => !startDate || d >= startDate);
        return pct(done.length, target);
      }

      case 'task': {
        const task = allTasks.find(t => t?.id === goal.linkId);
        if (!task) return null;
        return task.completed ? { progress: 100, completed: true } : { progress: 0, completed: false };
      }

      /**
       * ⭐ An event is ONE dated occurrence, so this is binary — exactly like
       * `task`, and deliberately unlike `habit`.
       *
       * ⚠️ `quantity` is therefore ignored here. A goal of "four sessions" is not
       * one event repeated; it is a HABIT (counted) or four separate events. That
       * is the whole once / repeated / at-a-time distinction — collapsing it by
       * counting events would make Event a worse Habit.
       *
       * ⚠️ Not date-windowed, also matching `task`. Linking to an event that has
       * already happened reads 100% immediately, which is correct: the thing did
       * occur. The `linkStartedAt` window exists to stop a long-running HABIT
       * banking a year of history — a single occurrence has no history to bank.
       */
      case 'event': {
        const event = sources.events?.find(e => e?.id === goal.linkId);
        if (!event) return null;
        return event.completed ? { progress: 100, completed: true } : { progress: 0, completed: false };
      }

      case 'project': {
        const project = sources.projects?.find(p => p?.id === goal.linkId);
        if (!project) return null;
        if (project.status === 'completed') return { progress: 100, completed: true };
        const progress = Math.min(100, Math.max(0, Math.round(project.progress || 0)));
        return { progress, completed: false };
      }

      case 'routine': {
        const routine = sources.routines?.find(r => r?.id === goal.linkId);
        if (!routine) return null;
        // Routines keep no completion history, so this cannot be windowed by date.
        // Matches RequirementTracker's documented limitation.
        return pct(routine.completedCount || 0, target);
      }

      case 'folder': {
        const folderTasks = allTasks.filter(t => t?.folderId === goal.linkId);
        if (folderTasks.length === 0) return null;
        const completedInWindow = folderTasks.filter(
          t => t?.completed && (!startDate || !t.pointRecord?.earnedAt || t.pointRecord.earnedAt >= startDate)
        );
        // quantity > 1 means "complete N tasks from this folder"; otherwise clear the folder.
        if ((goal.quantity || 1) > 1) return pct(completedInWindow.length, target);
        return pct(completedInWindow.length, folderTasks.length);
      }

      /**
       * ⭐ A program's completion lives on its RUN, never on the program record
       * — `Program.programStatus` has never once been written to `'completed'`.
       * `programStanding` reads every run and takes the best, so finishing a
       * program pins it at 100 and starting it again cannot undo that.
       *
       * ⚠️ `null` (never started) still means "not derivable, leave the manual
       * toggle alone" — unchanged behaviour, and the reason this is a pure
       * widening rather than a behaviour change for anyone mid-run.
       */
      case 'program': {
        const standing = programStanding(goal.linkId, sources.programRuns);
        if (!standing) return null;
        return { progress: standing.progress, completed: standing.completed };
      }

      // 'ability' has no single completion signal — left to the manual toggle.
      default:
        return null;
    }
  } catch {
    return null;
  }
}
